import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getMyRecord } from './puzzleApi';
import RankBadge from './RankBadge';
import './CaseFile.css';

const fontsUrl = 'https://fonts.googleapis.com/css2?family=Special+Elite&family=Libre+Franklin:ital,wght@0,400;0,600;0,800;1,400&display=swap';

const OUTCOME_LABEL = { clean: 'Clean win', hinted: 'Win (hints used)', missed: 'Missed' };

const MyRecord = () => {
  const [state, setState] = useState({ status: 'loading' });
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getMyRecord(1)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'ready', player: data.player, hasMore: data.hasMore });
          setHistory(data.player ? data.history : []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: 'error', code: error.code || 'server_error' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = async () => {
    const nextPage = page + 1;

    try {
      const data = await getMyRecord(nextPage);
      setHistory((prev) => [...prev, ...data.history]);
      setState((prev) => ({ ...prev, hasMore: data.hasMore }));
      setPage(nextPage);
    } catch (error) {
      // Leave the list as-is; the button stays available to try again.
    }
  };

  return (
    <div className="case-file-page">
      <Helmet>
        <title>My Record | Jerry Evanoff</title>
        <meta name="robots" content="noindex" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href={fontsUrl} />
      </Helmet>

      <main className="cf-folder">
        <span className="cf-tab">DETECTIVE FILE</span>
        <h1>My Record</h1>

        {state.status === 'loading' && <p className="cf-brief">Loading…</p>}
        {state.status === 'error' && <p className="cf-error" role="alert">Couldn't load your record. Please try again.</p>}

        {state.status === 'ready' && !state.player && (
          <>
            <p className="cf-brief">You're not on the list yet. Play this week's case and join to start your record.</p>
            <p className="cf-back-links"><Link to="/case-file">This week's case</Link></p>
          </>
        )}

        {state.status === 'ready' && state.player && (
          <>
            <p className="cf-brief">
              <strong>{state.player.nickname}</strong>, currently <RankBadge rank={state.player.rank} />
            </p>

            <div className="cf-member cf-record-totals">
              <div><strong>{state.player.wins}</strong><span>Wins</span></div>
              <div><strong>{state.player.points}</strong><span>Points</span></div>
              <div><strong>{state.player.bestStreak}</strong><span>Best streak</span></div>
              <div><strong>{state.player.currentStreak}</strong><span>Current streak</span></div>
            </div>

            {state.player.nextRankIn !== null && (
              <p className="cf-progress-counter">
                {state.player.nextRankIn} more clean {state.player.nextRankIn === 1 ? 'solve' : 'solves'} until your next promotion — the name's a surprise.
              </p>
            )}

            <h2>Your trophy case</h2>
            <ul className="cf-trophy-case">
              {state.player.earnedRanks.map((rank) => (
                <li key={rank.name}>{rank.name}</li>
              ))}
            </ul>

            <h2>Recent cases</h2>
            <ul className="cf-history-list">
              {history.map((entry, index) => (
                <li key={`${entry.caseNumber}-${index}`} className={`cf-history-row cf-history-${entry.outcome}`}>
                  <span className="cf-history-title">Case #{entry.caseNumber}: {entry.title}</span>
                  <span className="cf-history-outcome">{OUTCOME_LABEL[entry.outcome]}{entry.outcome !== 'missed' ? ` · ${entry.points} pts` : ''}</span>
                </li>
              ))}
            </ul>

            {state.hasMore && (
              <button type="button" className="cf-hint-btn" onClick={loadMore}>Show older cases</button>
            )}

            <p className="cf-back-links">
              <Link to="/standings">See the standings</Link> · <Link to="/case-file">This week's case</Link>
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default MyRecord;
